import { BookOfTheMonthBookBg } from 'assets';
import CouponCutEffectIconLeft from 'assets/Icons/CouponCutEffectIconLeft';
import CouponCutEffectIconRight from 'assets/Icons/CouponCutEffectIconRight';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { FC, useState } from 'react';
import { BiCopy, BiSolidCopy } from 'react-icons/bi';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';
import OfferText from '@/components/HomePage/BookOfTheMonth/OfferText';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';

import { Endpoints } from '@/network';
import { generateUrlForImage } from '@/pages/books/[bookId]';
import { IBestsellerSpotlight } from '@/utils/interfaces/BestsellerSpotlight';

interface Props {
  bestsellerSpotlight: IBestsellerSpotlight;
}

const BookOfTheMonth: FC<Props> = ({ bestsellerSpotlight }) => {
  const router = useRouter();
  const [copied, setCopied] = useState<string | null>(null);
  const twScreen = useTailwindBreakpoint();

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);

      setTimeout(() => {
        setCopied(null);
      }, 5000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };
  return (
    <>
      <Head>
        <link
          href='https://fonts.googleapis.com/css2?family=Pacifico&display=swap'
          rel='stylesheet'
        />
      </Head>
      <div className='h-full'>
        <div className='h-full border border-grayZl-850 rounded-lg flex overflow-hidden w-full md:flex-row flex-col '>
          <div className='xl:w-[356px] md:w-[300px] w-full md:h-full h-[298px] relative md:border-0 border-b border-grayZl-850'>
            <Image
              className=' h-full w-full '
              src={BookOfTheMonthBookBg}
              alt='BookOfTheMonthBookBg'
              width={356}
              height={436}
            />
            <div className='absolute top-0 left-0 w-full h-full flex items-center justify-center'>
              <Image
                className='sm:w-[166px] w-[154px] h-auto object-contain rounded'
                src={generateUrlForImage(
                  `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
                  `${Endpoints.getCoverImagesForBooks}/`,
                  `${bestsellerSpotlight.bookId?._id}`,
                )}
                alt={bestsellerSpotlight?.bookId?.title}
                width={166}
                height={235}
              />
            </div>
            <RenderIf condition={!twScreen.lg && !!bestsellerSpotlight?.offerText}>
              <OfferText
                className='absolute top-0 right-0 sm:mx-3 sm:my-3 mx-2 my-1 '
                key={1}
                offerText={bestsellerSpotlight?.offerText ?? ''}
              />
            </RenderIf>
          </div>
          <div className='relative xl:px-12 xl:py-12 md:px-5 md:py-5 px-3.5 py-6 pb-4  flex flex-col justify-between flex-1 items-start xl:gap-10 md:gap-5 gap-3.5'>
            <div className='xl:space-y-6 md:space-y-3 space-y-5 w-full '>
              <div className='flex items-center justify-between'>
                {bestsellerSpotlight?.badgeText ? (
                  <span
                    style={{ fontFamily: "'Pacifico', cursive" }}
                    className='md:text-2xl text-sm text-white leading-tight'
                  >
                    {bestsellerSpotlight.badgeText}
                  </span>
                ) : null}
                <div className='flex items-center gap-3'>
                  <RenderIf condition={twScreen.lg && !!bestsellerSpotlight?.offerText}>
                    <OfferText
                      key={1}
                      offerText={bestsellerSpotlight?.offerText ?? ''}
                    />
                  </RenderIf>
                  <RenderIf condition={!!bestsellerSpotlight?.couponCode}>
                    <div className='relative flex items-center sm:h-10 h-[28px] bg-orangeZl-950'>
                      <p className='lg:block hidden text-sm/[22px] font-normal text-grayZl-600 px-4 py-[9px] h-full border-r-2 border-dashed border-white/20'>
                        Coupon
                      </p>
                      <div className='h-full flex items-center gap-2.5 ps-[9px] pe-[11px]'>
                        <p className='sm:text-sm text-xs text-white font-normal line-clamp-1 truncate '>
                          {bestsellerSpotlight.couponCode}
                        </p>
                        <button
                          onClick={() => handleCopy(bestsellerSpotlight.couponCode ?? '')}
                          className='text-white'
                        >
                          {copied === bestsellerSpotlight.couponCode ? (
                            <BiSolidCopy className='sm:w-5 w-4 h-4 sm:h-5  text-white' />
                          ) : (
                            <BiCopy className='sm:w-5 w-4 h-4 sm:h-5 text-white' />
                          )}
                        </button>
                      </div>
                      <CouponCutEffectIconLeft className='absolute top-0 sm:-left-[5px] -left-[4px] h-full' />
                      <CouponCutEffectIconRight className='absolute top-0 sm:-right-[5px] -right-[4px]  h-full' />
                    </div>
                  </RenderIf>
                </div>
              </div>
              <div>
                <p className='xl:text-[32px] md:text-2xl  text-2xl text-white font-semibold xl:mb-2 md:mb-1 mb-2 line-clamp-2'>
                  {bestsellerSpotlight?.bookId?.title}
                </p>
                <p className='xl:text-lg md:text-sm text-sm font-medium text-white xl:mb-5 md:mb-3 mb-2 capitalize'>
                  By {bestsellerSpotlight?.bookId?.authors?.[0]}
                </p>
                <p className='xl:text-lg md:text-sm text-sm/[24px] font-light text-grayZl-500 line-clamp-4'>
                  {bestsellerSpotlight?.bookId?.longDescription}
                </p>
              </div>
            </div>
            <ButtonDefault
              onClick={() =>
                router.push(
                  `/books/${bestsellerSpotlight?.bookId?.slug}-${bestsellerSpotlight?.bookId?._id}`,
                )
              }
              size={0}
              className='xl:py-3 md:py-1.5 py-2 xl:px-4 md:px-2 px-4 md:text-sm text-sm font-semibold'
              variant={ButtonVariants.WHITE}
            >
              View Book
            </ButtonDefault>
            <div className='absolute top-0 left-0 w-full h-full bg-gradient-to-r from-[#121212] to-[#000000] -z-10'></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookOfTheMonth;
